import { AxiosInstance, AxiosRequestConfig, AxiosResponse, isAxiosError } from "axios";
import { Experience, Pricing, ExperienceType, RefundPolicy } from "@/types/generated/OfferServiceApiOld";
import {
  Option,
  ExperiencePaxes,
  Pricing as NewPricing,
  OptionPaxes,
  OptionLanguages,
  ExperienceLanguages,
  ExperienceZones,
  Allotment,
  InternalPricing,
  Release,
  StopSale,
  SharedAllotment,
  SharedAllotmentArchiveValidationErrorDto,
  AllotmentsSummary,
  SharedAllotmentPut,
  SharedAllotmentCollection,
  PricingHistory,
  AllotmentHistory,
  StopSaleHistory,
  OptionHistory,
  ReleaseHistory,
  OperationalExperience,
  ExperiencePostV3,
  ExperienceV3,
} from "@/types/generated/OfferServiceApi";
import { useAuthStore } from "@/features/auth/useAuthStore";
import { createHttpInstance } from "@/features/core-shared/utils/http/http";
import { AvailabilityType } from "@/features/experience-calendar/types/Availability";
import { INSTANT_DURATION } from "@/constants/date.constants";
import { emit } from "@rsql/emitter";
import builder from "@rsql/builder";
import { SharedAllotmentPost } from "@/features/shared-allotment/types";

interface Options {
  config: AxiosRequestConfig;
}

const ACCEPT_VERSION_2_HEADER_VALUE = "vnd.offer-experience-offer-service.v2";
const ACCEPT_VERSION_3_HEADER_VALUE = "vnd.offer-experience-offer-service.v3";

function getExperienceTypeTicketEndpoint(type: ExperienceType) {
  let endpoint: string;

  switch (type) {
    case "CALENDAR-TIMESLOTS":
      endpoint = "/datetime-tickets";
      break;
    case "CALENDAR-NO-TIMESLOTS":
      endpoint = "/date-tickets";
      break;
    case ExperienceType.NO_CALENDAR_FIXED_END:
      endpoint = "/open-tickets";
      break;
    case ExperienceType.NO_CALENDAR_FIXED_VALIDITY:
      endpoint = "/open-tickets";
      break;
    default:
      endpoint = "/datetime-tickets";
      break;
  }

  return endpoint;
}

function getPricings(http: AxiosInstance) {
  return async (experienceId: string, optionId: string, options?: Options) => {
    const { config } = options || {};

    const { data } = await http.get<Pricing[]>(`/pricings`, {
      params: {
        experience: experienceId,
        option: optionId,
      },
      ...config,
    });

    return { data };
  };
}

function createPricing(http: AxiosInstance) {
  return async (pricing: Pricing | NewPricing, options?: Options) => {
    const { config } = options || {};

    const { data } = await http.post<Pricing | NewPricing>(
      `/pricings`,
      {
        ...pricing,
      },
      config
    );

    return { data };
  };
}

function updatePricing(http: AxiosInstance) {
  return async (pricing: Pricing | NewPricing, options?: Options) => {
    const { config } = options || {};

    const { data } = await http.put<Pricing | NewPricing>(
      `/pricings/${pricing.id}`,
      {
        ...pricing,
      },
      config
    );

    return { data };
  };
}

function deletePricing(http: AxiosInstance) {
  return async (pricingId: string, options?: Options) => {
    const { config } = options || {};

    const { data } = await http.delete<boolean>(`/pricings/${pricingId}`, config);

    return { data };
  };
}

function getAvailabilities(http: AxiosInstance) {
  return async <T extends AvailabilityType>(optionId: string, type: ExperienceType, options?: Options) => {
    const endpoint = getExperienceTypeTicketEndpoint(type);
    const { config } = options || {};
    const { data } = await http.get<T[]>(endpoint, {
      params: { option: optionId },
      ...config,
    });

    return { data };
  };
}

function createAvailability(http: AxiosInstance) {
  return async <D extends AvailabilityType>(availability: Omit<D, "id">, type: ExperienceType, options?: Options) => {
    const endpoint = getExperienceTypeTicketEndpoint(type);
    const { config } = options || {};

    const { data } = await http.post<D>(endpoint, availability, config);

    return { data };
  };
}

function updateAvailability(http: AxiosInstance) {
  return async <T extends AvailabilityType>(availability: T, type: ExperienceType, options?: Options) => {
    const endpoint = getExperienceTypeTicketEndpoint(type);
    const { config } = options || {};

    const { data } = await http.put<T>(`${endpoint}/${availability.id}`, availability, config);

    return { data };
  };
}

function deleteAvailability(http: AxiosInstance) {
  return async (id: string, type: ExperienceType, options?: Options) => {
    const endpoint = getExperienceTypeTicketEndpoint(type);
    const { config } = options || {};

    const { data } = await http.delete<boolean>(`${endpoint}/${id}`, config);

    return { data };
  };
}

function getRefundPolicies(http: AxiosInstance) {
  return async (experienceId: string, options?: Options) => {
    const { config } = options || {};

    const { data } = await http.get<RefundPolicy[]>(`/refund-policies`, {
      params: {
        experience: experienceId,
      },
      ...config,
    });

    return { data };
  };
}

function postRefundPolicy(http: AxiosInstance) {
  return async (policy: RefundPolicy, options?: Options) => {
    const { config } = options || {};

    const { data } = await http.post<never, AxiosResponse<RefundPolicy>, Omit<RefundPolicy, "id">>(
      `/refund-policies`,
      {
        experience: policy.experience,
        period: policy.period,
        value: policy.value,
        refund_type_code: policy.refund_type_code,
      },
      {
        ...config,
      }
    );

    return data;
  };
}

function putRefundPolicy(http: AxiosInstance) {
  return async (policy: RefundPolicy, options?: Options) => {
    if (!policy.id) {
      throw new Error("Cannot update. Policy ID not provided");
    }

    const { config } = options || {};

    await http.put(`/refund-policies/${policy.id}`, policy, {
      ...config,
    });
  };
}

function deleteRefundPolicy(http: AxiosInstance) {
  return async (policyId: string, options?: Options) => {
    const { config } = options || {};

    await http.delete(`/refund-policies/${policyId}`, {
      ...config,
    });
  };
}

function updateExperienceLanguages(http: AxiosInstance) {
  return async (payload: ExperienceLanguages, options?: Options) => {
    const { config } = options || {};

    const { data } = await http.put<ExperienceLanguages>(
      `/experiences/${payload.experience_id}/experience-languages`,
      payload,
      { ...config, headers: { "accept-version": ACCEPT_VERSION_2_HEADER_VALUE } }
    );
    return { data };
  };
}

function getExperiencePaxes(http: AxiosInstance) {
  return async (experienceId: string, options?: Options) => {
    const { config } = options || {};
    const { data } = await http.get<ExperiencePaxes>(`/experiences/${experienceId}/experience-paxes`, {
      ...config,
      headers: { "accept-version": ACCEPT_VERSION_2_HEADER_VALUE },
    });
    return { data };
  };
}

function updateExperiencePaxes(http: AxiosInstance) {
  return async (payload: ExperiencePaxes, options?: Options) => {
    const { config } = options || {};

    const { data } = await http.put<ExperiencePaxes>(
      `/experiences/${payload.experience_id}/experience-paxes`,
      payload,
      { ...config, headers: { "accept-version": ACCEPT_VERSION_2_HEADER_VALUE } }
    );
    return { data };
  };
}

function getOptionPaxes(http: AxiosInstance) {
  return async (optionId: string, options?: Options) => {
    const { config } = options || {};
    const { data } = await http.get<OptionPaxes>(`/options/${optionId}/option-paxes`, config);
    return { data };
  };
}

function putOptionPaxes(http: AxiosInstance) {
  return async (optionId: string, optionPaxes: OptionPaxes, options?: Options) => {
    const { config } = options || {};
    const { data } = await http.put<OptionPaxes>(`/options/${optionId}/option-paxes`, optionPaxes, config);
    return { data };
  };
}

function getOptionLanguages(http: AxiosInstance) {
  return async (optionId: string, options?: Options) => {
    const { config } = options || {};
    const { data } = await http.get<OptionLanguages>(`/options/${optionId}/option-languages`, {
      ...config,
      headers: { ...config?.headers, "accept-version": ACCEPT_VERSION_2_HEADER_VALUE },
    });
    return { data };
  };
}

function putOptionLanguages(http: AxiosInstance) {
  return async (optionId: string, optionLanguages: OptionLanguages, options?: Options) => {
    const { config } = options || {};
    const { data } = await http.put<OptionLanguages>(`/options/${optionId}/option-languages`, optionLanguages, {
      ...config,
      headers: { ...config?.headers, "accept-version": ACCEPT_VERSION_2_HEADER_VALUE },
    });
    return { data };
  };
}

function getExperienceLanguages(http: AxiosInstance) {
  return async (experienceId: string, options?: Options) => {
    const { config } = options || {};
    const { data } = await http.get<ExperienceLanguages>(`/experiences/${experienceId}/experience-languages`, {
      ...config,
      headers: { "accept-version": ACCEPT_VERSION_2_HEADER_VALUE },
    });
    return { data };
  };
}

function getAllotments(http: AxiosInstance) {
  return async (experienceId: string, options?: Options) => {
    const { config } = options || {};
    const { data } = await http.get<Allotment[]>(`/allotments`, {
      ...config,
      params: {
        experience: experienceId,
      },
      headers: { "accept-version": ACCEPT_VERSION_2_HEADER_VALUE },
    });
    return { data };
  };
}

function getAllotmentsBySharedAllotment(http: AxiosInstance) {
  return async (sharedAllotmentId: string, filters?: string, options?: Options) => {
    const { config } = options || {};
    const { data } = await http.get<Allotment[]>(`/allotments`, {
      ...config,
      params: {
        shared_allotment: sharedAllotmentId,
        // RSQL filters string; axios URL-encodes params automatically.
        filters: filters || undefined,
      },
      headers: { ...config?.headers, "accept-version": ACCEPT_VERSION_2_HEADER_VALUE },
    });
    return { data };
  };
}

export interface AllotmentsSummaryParams {
  filter?: string;
  languages?: string[];
  zones?: string[];
  dateFrom?: string;
  dateTo?: string;
  dates?: string;
  daysOfWeek?: string[];
}

function getAllotmentsSummary(http: AxiosInstance) {
  return async (experienceId: string, params?: AllotmentsSummaryParams, options?: Options) => {
    const { config } = options || {};
    const { data } = await http.get<AllotmentsSummary>(`/experiences/${experienceId}/allotments-summary`, {
      ...config,
      headers: { ...config?.headers, "accept-version": ACCEPT_VERSION_2_HEADER_VALUE },
      params: {
        filter: params?.filter,
        languages: params?.languages?.join(",") || undefined,
        zones: params?.zones?.join(",") || undefined,
        dateFrom: params?.dateFrom,
        dateTo: params?.dateTo,
        dates: params?.dates,
        daysOfWeek: params?.daysOfWeek?.join(",") || undefined,
      },
    });
    return { data };
  };
}

function getExperienceZones(http: AxiosInstance) {
  return async (experienceId: string, options?: Options) => {
    const { config } = options || {};
    const { data } = await http.get<ExperienceZones>(`/experiences/${experienceId}/experience-zones`, {
      ...config,
      headers: { "accept-version": ACCEPT_VERSION_2_HEADER_VALUE },
    });
    return { data };
  };
}

function updateExperienceZones(http: AxiosInstance) {
  return async (payload: ExperienceZones, options?: Options) => {
    const { config } = options || {};

    const { data } = await http.put<ExperienceZones>(
      `/experiences/${payload.experience_id}/experience-zones`,
      payload,
      { ...config, headers: { "accept-version": ACCEPT_VERSION_2_HEADER_VALUE } }
    );
    return { data };
  };
}

function createAllotment(http: AxiosInstance) {
  return async (allotment: Allotment) => {
    const { data, headers } = await http.post<Allotment>(`/allotments`, allotment);
    return { data, headers };
  };
}

function updateAllotment(http: AxiosInstance) {
  return async (allotment: Allotment) => {
    if (!allotment.id) {
      throw new Error("the Allotment you are updating has an invalid id");
    }
    const { data } = await http.put(`/allotments/${allotment.id}`, allotment);
    return { data };
  };
}

function deleteAllotment(http: AxiosInstance) {
  return async (id: string, options?: Options) => {
    const { config } = options || {};

    return await http.delete(`/allotments/${id}`, config);
  };
}

function getReleases(http: AxiosInstance) {
  return async (experienceId: string, options?: Options) => {
    const { config } = options || {};
    const { data } = await http.get<Release[]>("/releases", {
      ...config,
      params: {
        filters: emit(builder.eq("experience_id", experienceId)),
      },
      headers: { "accept-version": ACCEPT_VERSION_2_HEADER_VALUE },
    });
    return { data };
  };
}

function createRelease(http: AxiosInstance) {
  return async (release: Omit<Release, "id">, options?: Options) => {
    const { config } = options || {};

    const { data } = await http.post<Release>("/releases", release, config);

    return { data };
  };
}

function getRelease(http: AxiosInstance) {
  return async (id: string, options?: Options) => {
    const { config } = options || {};

    const { data } = await http.get<Release>(`/releases/${id}`, config);

    return { data };
  };
}

function updateRelease(http: AxiosInstance) {
  return async (release: Release, options?: Options) => {
    if (!release.id) {
      throw new Error("Cannot update. Release ID not provided");
    }

    const { config } = options || {};

    const { data } = await http.put<Release>(`/releases/${release.id}`, release, config);

    return { data };
  };
}

function deleteRelease(http: AxiosInstance) {
  return async (id: string, options?: Options) => {
    const { config } = options || {};

    return await http.delete(`/releases/${id}`, config);
  };
}

function getInternalPricings(http: AxiosInstance) {
  return async (optionIds: string[], options?: Options) => {
    const { config } = options || {};
    const { data } = await http.get<InternalPricing[]>(`/internal-product/pricings`, {
      params: {
        filters: emit(builder.in("option_id", optionIds)),
      },
      ...config,
    });
    return { data };
  };
}

function postInternalProductPricing(http: AxiosInstance) {
  return async (internalProductPricing: InternalPricing) => {
    const { data, headers } = await http.post<InternalPricing>(`/internal-product/pricings`, internalProductPricing);
    return { data, headers };
  };
}

function putInternalProductPricing(http: AxiosInstance) {
  return async (internalProductPricing: InternalPricing) => {
    if (!internalProductPricing.id) {
      throw new Error("the InternalPricing you are updating has an invalid id");
    }
    const { data } = await http.put(`/internal-product/pricings/${internalProductPricing.id}`, internalProductPricing);
    return { data };
  };
}

function deleteInternalProductPricing(http: AxiosInstance) {
  return async (id: string) => {
    return await http.delete(`/internal-product/pricings/${id}`);
  };
}

function getPricingHistory(http: AxiosInstance) {
  return async (params: { experienceId: string; filters?: string; offset?: number; limit?: number; sort?: string }) => {
    const { data } = await http.get<PricingHistory[]>("/pricings/history", {
      params: {
        experience: params.experienceId,
        filters: params.filters,
        offset: params.offset,
        limit: params.limit,
        sort: params.sort,
      },
    });
    return { data };
  };
}

function getAllotmentHistory(http: AxiosInstance) {
  return async (params: { experienceId: string; filters?: string; offset?: number; limit?: number; sort?: string }) => {
    const { data } = await http.get<AllotmentHistory[]>("/allotments/history", {
      params: {
        filters: params.filters,
        offset: params.offset,
        limit: params.limit,
        sort: params.sort,
      },
    });
    return { data };
  };
}

function getStopSalesHistory(http: AxiosInstance) {
  return async (params: { filters?: string; offset?: number; limit?: number; sort?: string }) => {
    const { data } = await http.get<StopSaleHistory[]>("/stop-sales/history", {
      params: {
        filters: params.filters,
        offset: params.offset,
        limit: params.limit,
        sort: params.sort,
      },
    });
    return { data };
  };
}

function getOptionsHistory(http: AxiosInstance) {
  return async (params: { experienceId: string; filters?: string; offset?: number; limit?: number; sort?: string }) => {
    const { data } = await http.get<OptionHistory[]>("/options/history", {
      params: {
        filters: params.filters,
        offset: params.offset,
        limit: params.limit,
        sort: params.sort,
      },
    });
    return { data };
  };
}

function getReleaseHistory(http: AxiosInstance) {
  return async (params: { filters?: string; offset?: number; limit?: number; sort?: string }) => {
    const { data } = await http.get<ReleaseHistory[]>("/releases/history", {
      params: {
        filters: params.filters,
        offset: params.offset,
        limit: params.limit,
        sort: params.sort,
      },
    });
    return { data };
  };
}

function getAllStopSales(http: AxiosInstance) {
  return async (experienceId: string, options?: Options) => {
    const { config } = options || {};
    const { data } = await http.get<StopSale[]>("/stop-sales", {
      ...config,
      params: {
        filters: emit(builder.eq("experience_id", experienceId)),
      },
    });
    return { data };
  };
}

function getStopSale(http: AxiosInstance) {
  return async (id: string, options?: Options) => {
    const { config } = options || {};
    const { data } = await http.get<StopSale>(`/stop-sales/${id}`, config);
    return { data };
  };
}

function createStopSale(http: AxiosInstance) {
  return async (stopSale: Omit<StopSale, "id">, options?: Options) => {
    const { config } = options || {};
    const { data, headers } = await http.post<StopSale>("/stop-sales", stopSale, config);
    return { data, headers };
  };
}

function updateStopSale(http: AxiosInstance) {
  return async (id: string, stopSale: StopSale, options?: Options) => {
    const { config } = options || {};
    const { data } = await http.put<StopSale>(`/stop-sales/${id}`, stopSale, config);
    return { data };
  };
}

function deleteStopSale(http: AxiosInstance) {
  return async (id: string, options?: Options) => {
    const { config } = options || {};
    return await http.delete(`/stop-sales/${id}`, config);
  };
}

function getSharedAllotments(http: AxiosInstance) {
  return async (filters?: string, options?: Options) => {
    const { config } = options || {};
    const { data } = await http.get<SharedAllotmentCollection[]>(`/shared-allotments`, {
      ...config,
      headers: { ...config?.headers, "accept-version": ACCEPT_VERSION_2_HEADER_VALUE },
      params: {
        ...config?.params,
        "include-linked-items": true,
        ...(filters ? { filters } : {}),
      },
    });
    return { data };
  };
}

interface OperationalExperiencesParams {
  offset: number;
  limit: number;
  filters?: string;
  search_code?: string;
  search_name?: string;
  sort?: string;
}

function getOperationalExperiences(http: AxiosInstance) {
  return async (params: OperationalExperiencesParams, options?: Options) => {
    const { config } = options || {};
    const { data, headers } = await http.get<OperationalExperience[]>(`/operational-experiences`, {
      ...config,
      headers: { ...config?.headers, "accept-version": ACCEPT_VERSION_3_HEADER_VALUE },
      params: { ...config?.params, ...params },
    });
    return { data, headers };
  };
}

export type OperationalExperiencePost = Required<
  Pick<ExperiencePostV3, "code" | "name" | "company_code" | "office_code" | "incoming_office">
>;

function createOperationalExperience(http: AxiosInstance) {
  return async (payload: OperationalExperiencePost, options?: Options) => {
    const { config } = options || {};
    const body: Omit<ExperiencePostV3, "id"> = {
      type: "CALENDAR-TIMESLOTS",
      confirmation_time: INSTANT_DURATION,
      cutoff_time: INSTANT_DURATION,
      currency: "EUR",
      experience_source: "INTERNAL",
      ...payload,
    };
    try {
      const { data } = await http.post<ExperienceV3>("/experiences", body, {
        ...config,
        headers: { ...config?.headers, "accept-version": ACCEPT_VERSION_3_HEADER_VALUE },
      });
      return { data };
    } catch (error: unknown) {
      if (isAxiosError<{ code?: string }>(error) && error.response?.data?.code === "ConflictApiException") {
        throw new DuplicatedCodeError();
      }
      throw error;
    }
  };
}

function getSharedAllotment(http: AxiosInstance) {
  return async (id: string, options?: Options) => {
    const { config } = options || {};
    const { data } = await http.get<SharedAllotment>(`/shared-allotments/${id}`, config);
    return { data };
  };
}

function createSharedAllotment(http: AxiosInstance) {
  return async (sharedAllotment: SharedAllotmentPost, options?: Options) => {
    const DUPLICATED_CODE_ERROR: string = "ConflictApiException";
    const { config } = options || {};
    const sharedAllotmentBodyData: Record<string, unknown> = {
      name: sharedAllotment.name,
      code: sharedAllotment.code,
      ...(sharedAllotment.companyCode &&
        sharedAllotment.incomingOffice &&
        sharedAllotment.officeCode && {
          company_code: sharedAllotment.companyCode,
          incoming_office: sharedAllotment.incomingOffice,
          office_code: sharedAllotment.officeCode,
        }),
    };
    try {
      const { data } = await http.post<SharedAllotment>(`/shared-allotments`, sharedAllotmentBodyData, config);
      return { data };
    } catch (err: any) {
      const errorCode = err?.response?.data?.code;
      if (errorCode === DUPLICATED_CODE_ERROR) {
        throw new DuplicatedCodeError();
      }
      throw err;
    }
  };
}

function updateSharedAllotment(http: AxiosInstance) {
  return async (id: string, sharedAllotment: SharedAllotmentPut, options?: Options) => {
    const { config } = options || {};
    const { data } = await http.put<SharedAllotment>(`/shared-allotments/${id}`, sharedAllotment, config);
    return { data };
  };
}

function archiveSharedAllotment(http: AxiosInstance) {
  return async (id: string, options?: Options) => {
    const { config } = options || {};
    try {
      const { data } = await http.post<SharedAllotment>(`/shared-allotments/${id}/archive`, undefined, config);
      return { data };
    } catch (err: any) {
      if (err?.response?.status === 409) {
        throw new ArchiveSharedAllotmentConflictError(err.response.data as SharedAllotmentArchiveValidationErrorDto);
      }
      throw err;
    }
  };
}

function unarchiveSharedAllotment(http: AxiosInstance) {
  return async (id: string, options?: Options) => {
    const { config } = options || {};
    const { data } = await http.post<SharedAllotment>(`/shared-allotments/${id}/unarchive`, undefined, config);
    return { data };
  };
}

export class DuplicatedCodeError extends Error {}

export class ArchiveSharedAllotmentConflictError extends Error {
  constructor(public readonly validationError: SharedAllotmentArchiveValidationErrorDto) {
    super(validationError.message);
  }
}

/**
 * Offer Service interface
 * @returns _axiosInstance - Original axios instance, to be used only for internal purpose
 * @returns getAllOptions - Request all the available options
 * @returns getExperienceOptions - Request all the available options for a given experience ID
 * @returns createExperience - Creates an experience in the offer service
 * @returns getExperience - Gets an experience in the offer service
 * @returns postOption - Creates a single option
 * @returns getOption - Request a single option data by its ID
 * @returns putOption - Update a single option data by its ID
 * @returns deleteOption - Delete a single option data by its ID
 * @returns getAllReleases - Request all releases
 * @returns createRelease - Creates a new release
 * @returns getRelease - Gets a specific release by its ID
 * @returns updateRelease - Updates an existing release
 * @returns deleteRelease - Deletes a release by its ID
 */
export function useOfferServiceApi() {
  const runtimeConfig = useRuntimeConfig();
  const auth = useAuthStore();

  const axiosInstance = createHttpInstance({
    config: {
      baseURL: runtimeConfig.public.OFFER_SERVICE_BASE_URL,
      headers: {
        Accept: "application/json",
      },
    },
    auth,
  });

  async function createExperience(experienceId: string, payload: Partial<Experience>, options?: Options) {
    const { config } = options || {};
    const body: Experience = {
      type: ExperienceType.CALENDAR_TIMESLOTS,
      confirmation_time: INSTANT_DURATION,
      cutoff_time: INSTANT_DURATION,
      supplier: runtimeConfig.public.SUPPLIER_ID,
      state: "DRAFT",
      currency: "EUR",
      uuid: experienceId,
      ...payload,
    };

    await axiosInstance.post<Experience>("/experiences", body, config);

    return { data: body };
  }

  function getExperience(experienceId: string, options?: Options) {
    const { config } = options || {};
    return axiosInstance.get<Experience>(`/experiences/${experienceId}`, config);
  }

  async function updateExperience(experienceId: string, data: Partial<Experience>, options?: Options) {
    const DUPLICATED_CODE_ERROR: string = "ConflictApiException";
    const { config } = options || {};
    try {
      return await axiosInstance.put<Experience>(`/experiences/${experienceId}`, data, config);
    } catch (err: any) {
      const errorCode = err?.response?.data?.code;
      if (errorCode === DUPLICATED_CODE_ERROR) {
        throw new DuplicatedCodeError();
      }

      throw err;
    }
  }

  function publishExperience(experienceId: string, options?: Options) {
    const { config } = options || {};

    return axiosInstance.post<void>(`/experiences/${experienceId}/publish`, {}, config);
  }

  function deleteExperience(experienceId: string, options?: Options) {
    const { config } = options || {};

    return axiosInstance.delete<Experience>(`/experiences/${experienceId}`, config);
  }

  function getExperienceOptions(experienceId: string, options?: Options) {
    const { config } = options || {};
    return axiosInstance.get<Option[]>("/options", {
      ...config,
      params: {
        experience: experienceId,
        ...config?.params,
      },
    });
  }

  function getAllOptions(options?: Options) {
    const { config } = options || {};
    return axiosInstance.get<Option[]>("/options", config);
  }

  function getOptionPricing(optionId: string, options?: Options) {
    const { config } = options || {};
    return axiosInstance.get<Pricing[]>(`/pricings`, {
      ...config,
      params: {
        option: optionId,
        ...config?.params,
      },
    });
  }

  function postOption(experienceId: string, data: Omit<Option, "experience" | "id">, options?: Options) {
    const { config } = options || {};

    return axiosInstance.post<Option>(
      "/options",
      {
        ...data,
        experience: experienceId,
      },
      config
    );
  }

  function getOption(optionId: string, options?: Options) {
    const { config } = options || {};

    return axiosInstance.get<Option>(`/options/${optionId}`, config);
  }

  function getOptions(experienceId: string, params?: { activationStatus?: "ACTIVE" | "ARCHIVED" }, options?: Options) {
    const { config } = options || {};

    const filters =
      params?.activationStatus !== undefined
        ? emit(builder.eq("activation_status", params.activationStatus))
        : undefined;

    return axiosInstance.get<Option[]>(`/options`, {
      params: {
        experience: experienceId,
        ...(filters ? { filters } : {}),
      },
      ...config,
    });
  }

  function getOptionsByIds(optionIds: string[], options?: Options) {
    const { config } = options || {};
    return axiosInstance.get<Option[]>(`/options`, {
      params: {
        filters: emit(builder.in("id", optionIds)),
      },
      ...config,
    });
  }

  function putOption(optionId: string, data: Option, options?: Options) {
    const { config } = options || {};
    return axiosInstance.put<Option>(`/options/${optionId}`, data, config);
  }

  function deleteOption(optionId: string, options?: Options) {
    const { config } = options || {};
    return axiosInstance.delete<Option>(`/options/${optionId}`, config);
  }

  function archiveOption(optionId: string, options?: Options) {
    const { config } = options || {};
    return axiosInstance.post<Option>(`/options/${optionId}/archive`, undefined, config);
  }

  function unarchiveOption(optionId: string, options?: Options) {
    const { config } = options || {};
    return axiosInstance.post<Option>(`/options/${optionId}/unarchive`, undefined, config);
  }

  return {
    _axiosInstance: axiosInstance,
    createExperience,
    getExperience,
    updateExperience,
    publishExperience,
    deleteExperience,
    getExperienceOptions,
    getAllOptions,
    getOption,
    getOptions,
    getOptionsByIds,
    getOptionPricing,
    postOption,
    putOption,
    deleteOption,
    archiveOption,
    unarchiveOption,
    getPricings: getPricings(axiosInstance),
    deletePricing: deletePricing(axiosInstance),
    createPricing: createPricing(axiosInstance),
    updatePricing: updatePricing(axiosInstance),
    getAvailabilities: getAvailabilities(axiosInstance),
    createAvailability: createAvailability(axiosInstance),
    updateAvailability: updateAvailability(axiosInstance),
    deleteAvailability: deleteAvailability(axiosInstance),
    getRefundPolicies: getRefundPolicies(axiosInstance),
    postRefundPolicy: postRefundPolicy(axiosInstance),
    putRefundPolicy: putRefundPolicy(axiosInstance),
    deleteRefundPolicy: deleteRefundPolicy(axiosInstance),
    updateExperienceLanguages: updateExperienceLanguages(axiosInstance),
    getExperiencePaxes: getExperiencePaxes(axiosInstance),
    updateExperiencePaxes: updateExperiencePaxes(axiosInstance),
    getOptionPaxes: getOptionPaxes(axiosInstance),
    putOptionPaxes: putOptionPaxes(axiosInstance),
    getOptionLanguages: getOptionLanguages(axiosInstance),
    putOptionLanguages: putOptionLanguages(axiosInstance),
    getExperienceLanguages: getExperienceLanguages(axiosInstance),
    getExperienceZones: getExperienceZones(axiosInstance),
    updateExperienceZones: updateExperienceZones(axiosInstance),
    getAllotments: getAllotments(axiosInstance),
    getAllotmentsBySharedAllotment: getAllotmentsBySharedAllotment(axiosInstance),
    getAllotmentsSummary: getAllotmentsSummary(axiosInstance),
    createAllotment: createAllotment(axiosInstance),
    updateAllotment: updateAllotment(axiosInstance),
    deleteAllotment: deleteAllotment(axiosInstance),
    getReleases: getReleases(axiosInstance),
    createRelease: createRelease(axiosInstance),
    getRelease: getRelease(axiosInstance),
    updateRelease: updateRelease(axiosInstance),
    deleteRelease: deleteRelease(axiosInstance),
    getInternalPricings: getInternalPricings(axiosInstance),
    postInternalProductPricing: postInternalProductPricing(axiosInstance),
    putInternalProductPricing: putInternalProductPricing(axiosInstance),
    deleteInternalProductPricing: deleteInternalProductPricing(axiosInstance),
    getPricingHistory: getPricingHistory(axiosInstance),
    getAllotmentHistory: getAllotmentHistory(axiosInstance),
    getStopSalesHistory: getStopSalesHistory(axiosInstance),
    getOptionsHistory: getOptionsHistory(axiosInstance),
    getReleaseHistory: getReleaseHistory(axiosInstance),
    getAllStopSales: getAllStopSales(axiosInstance),
    getStopSale: getStopSale(axiosInstance),
    createStopSale: createStopSale(axiosInstance),
    updateStopSale: updateStopSale(axiosInstance),
    deleteStopSale: deleteStopSale(axiosInstance),
    getSharedAllotments: getSharedAllotments(axiosInstance),
    getOperationalExperiences: getOperationalExperiences(axiosInstance),
    createOperationalExperience: createOperationalExperience(axiosInstance),
    getSharedAllotment: getSharedAllotment(axiosInstance),
    createSharedAllotment: createSharedAllotment(axiosInstance),
    updateSharedAllotment: updateSharedAllotment(axiosInstance),
    archiveSharedAllotment: archiveSharedAllotment(axiosInstance),
    unarchiveSharedAllotment: unarchiveSharedAllotment(axiosInstance),
  };
}
